/** @format */
// Derive a gym slug from the browser hostname — the app-layer half of the
// per-gym subdomain architecture (WGO-F090).
//
// CONTEXT: `*.wtfgos.com` DNS + the Front Door wildcard route have been live
// since 2026-07-21, but nothing in the SPA ever read the hostname, so every
// subdomain served the identical generic marketing site. This module is the
// missing read.
//
// NOTE on X-Forwarded-Host: WTF-GO-PRODUCTION-MASTER.md §9 says the SPA "must
// read the X-Forwarded-Host header". That is not how a statically-served SPA
// works — Front Door rewrites the ORIGIN host header (so the SWA answers), but
// the BROWSER's address bar, and therefore window.location.hostname, is still
// `<gym>.wtfgos.com`. Reading the header would require a server render we do not
// have; reading location.hostname is both correct and simpler.
//
// FAILS CLOSED BY DESIGN: anything unrecognised returns null, which yields
// today's behaviour (the generic site). The alternative — guessing — would let
// `app.wtfgos.com` resolve to "a gym called app" and break the product for
// every user on the platform host.

// Platform hosts that live on the same wildcard and must never be read as gyms.
export const RESERVED_SUBDOMAINS = Object.freeze([
  "app",       // the product itself
  "api",       // backend
  "www",       // apex alias
  "admin",     // platform console
  "blog",      // Astro blog (subdomain-routed)
  "mail",
  "static",
  "assets",
  "cdn",
  "staging",
  "dev",
  "test",
  "preview",
  "docs",
  "help",
  "support",
  "status",
  "asuid",     // Azure custom-domain verification records
]);

const DEFAULT_APEX = "wtfgos.com";

// A DNS label: 1-63 chars, a-z/0-9, hyphens allowed but not at either end.
const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * @param {string} hostname e.g. window.location.hostname
 * @param {{apex?: string, reserved?: string[]}} [opts]
 * @returns {string|null} the gym slug, or null when the host is not a gym host
 */
export function gymSlugFromHost(hostname, opts = {}) {
  if (typeof hostname !== "string" || !hostname) return null;

  const apex = String(opts.apex || DEFAULT_APEX).toLowerCase();
  const reserved = new Set([...RESERVED_SUBDOMAINS, ...(opts.reserved || [])].map((s) => String(s).toLowerCase()));

  // strip port, a trailing root dot, and normalise case
  const host = hostname.toLowerCase().split(":")[0].replace(/\.$/, "");
  if (!host) return null;

  const suffix = `.${apex}`;
  if (!host.endsWith(suffix)) return null;          // different domain, or the apex itself

  const label = host.slice(0, -suffix.length);
  if (!label) return null;                           // the apex exactly
  if (label.includes(".")) return null;              // a.b.wtfgos.com is not a gym front door
  if (!LABEL_RE.test(label)) return null;            // not a valid DNS label (also blocks path/encoding tricks)
  if (reserved.has(label)) return null;              // platform host

  return label;
}

export default gymSlugFromHost;
